import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useAppServices } from "./services";
import useGhlSso from "./useGhlSso";

/**
 * Everything the app page needs to decide what to render, in two steps:
 *
 *   1. `useGhlSso` establishes who is looking — the signed-in GHL user and the
 *      sub-account currently open — from the SSO handshake with the parent
 *      frame, falling back to query params when the page isn't framed by GHL.
 *   2. `GET /app-installs/status` decides whether that account may see the page
 *      at all, and returns the app in the same round trip.
 *
 * `appId` comes from the path, because SSO identifies the user and the location
 * but never which app's custom page this is.
 */
export default function useInstall() {
  const { appId } = useParams();
  const { installs } = useAppServices();
  const sso = useGhlSso(appId);

  const [state, setState] = useState({
    loading: true,
    installed: false,
    app: null,
    install: null,
    error: null,
  });

  const { loading: ssoLoading, locationId, companyId, error: ssoError } = sso;

  const load = useCallback(async () => {
    // Wait for the handshake to settle — asking about the wrong (or an empty)
    // location would flash "not installed" before we know anything.
    if (ssoLoading) return;

    if (ssoError) {
      setState({
        loading: false,
        installed: false,
        app: null,
        install: null,
        error: ssoError,
      });
      return;
    }

    if (!appId) {
      setState({
        loading: false,
        installed: false,
        app: null,
        install: null,
        error: "No app was specified in the URL.",
      });
      return;
    }

    if (!locationId && !companyId) {
      setState({
        loading: false,
        installed: false,
        app: null,
        install: null,
        error:
          "No account context. Open this page from the app's entry in your CRM side nav.",
      });
      return;
    }

    setState((prev) => ({ ...prev, loading: true, error: null }));

    const query = new URLSearchParams({
      appId,
      ...(locationId && { locationId }),
      ...(companyId && { companyId }),
    }).toString();

    const { response, error } = await installs.Status(query);

    if (error) {
      setState({
        loading: false,
        installed: false,
        app: null,
        install: null,
        error: error?.message || "Could not check the install for this app.",
      });
      return;
    }

    setState({
      loading: false,
      installed: Boolean(response?.installed),
      app: response?.app || null,
      install: response?.install || null,
      error: null,
    });
    // `installs` is rebuilt every render by the services hook.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId, locationId, companyId, ssoLoading, ssoError]);

  useEffect(() => {
    load();
  }, [load]);

  return {
    ...state,
    // Stay loading through the handshake so the page never renders a decision
    // it hasn't got the inputs for yet.
    loading: ssoLoading || state.loading,
    appId,
    locationId,
    companyId,
    user: sso.user,
    contextSource: sso.source,
    reload: load,
  };
}
